import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, Chip, Divider, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { AutoAwesomeRounded, CheckCircleRounded, LockRounded, PaidOutlined, StorageRounded } from '@mui/icons-material';
import { ColorModeContext } from '../ColorModeContext';
import { changePassword, getCurrentUser } from '../features/account/api';
import FirmProfileCard from '../components/FirmProfileCard';
import { isNeoLegalFirm, neoFirmRates } from '../edition';

export default function SettingsPage() {
  const { mode, toggleColorMode } = React.useContext(ColorModeContext);
  const [identity, setIdentity] = useState(null);
  const [health, setHealth] = useState(null);
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const neoWorkspace = isNeoLegalFirm(identity?.organizationName);

  useEffect(() => {
    Promise.all([
      getCurrentUser(),
      fetch('/health', { credentials: 'include' }).then(response => response.json()),
    ]).then(([me, status]) => {
      setIdentity(me.user);
      setHealth(status);
    }).catch(err => setError(err.message));
  }, []);

  async function savePassword(event) {
    event.preventDefault();
    setError('');
    setNotice('');
    if (passwords.newPassword !== passwords.confirm) {
      setError('New passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await changePassword({ currentPassword: passwords.currentPassword, newPassword: passwords.newPassword });
      setPasswords({ currentPassword: '', newPassword: '', confirm: '' });
      setNotice('Password changed. Other signed-in devices were signed out.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto', pb: 5 }}>
      <Typography variant="overline" color="secondary.main">Your workspace</Typography>
      <Typography variant="h3">Settings</Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>Your account, firm connections, and security in one place.</Typography>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Stack spacing={2}>
        <FirmProfileCard identity={identity} />

        <Card sx={{ p: 2.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5">Firm membership</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>This account opens only the workspace and records belonging to your firm.</Typography>
            </Box>
            <Chip icon={<CheckCircleRounded />} color="success" label="Active member" />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <Box><Typography variant="caption" color="text.secondary">Signed in as</Typography><Typography fontWeight={750}>{identity?.name || 'Loading…'}</Typography><Typography variant="body2">{identity?.email}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Firm and role</Typography><Typography fontWeight={750}>{identity?.organizationName || 'Loading…'}</Typography><Typography variant="body2">{identity?.professionalTitle} · {identity?.organizationJurisdiction}</Typography></Box>
          </Box>
        </Card>

        <Card sx={{ p: 2.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={1.4} alignItems="center">
              <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: health?.aiConfigured ? '#E1F6EC' : '#FFF3DA', color: health?.aiConfigured ? 'success.main' : 'warning.main', display: 'grid', placeItems: 'center' }}><AutoAwesomeRounded /></Box>
              <Box><Typography variant="h5">Lex Maple AI for NEO</Typography><Typography variant="body2" color="text.secondary">Firm-level connection for Copilot and drafting—not a staff login.</Typography></Box>
            </Stack>
            <Chip color={health?.aiConfigured ? 'success' : 'warning'} label={health?.aiConfigured ? 'Connected' : 'Setup required'} />
          </Stack>
          <Alert severity={health?.aiConfigured ? 'success' : 'info'} sx={{ mt: 2 }}>
            {health?.aiConfigured
              ? 'The firm AI connection is ready. Staff use it inside authorized tools without signing in to OpenAI.'
              : 'A firm administrator must connect the server-side OpenAI project before AI actions can run. Core matters, intake, time, drafting, and team work remain available.'}
          </Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
            <Chip icon={<StorageRounded />} variant="outlined" label={health?.indexedSourceConnected ? 'Knowledge index connected' : 'Knowledge index not connected'} />
            <Chip icon={<LockRounded />} variant="outlined" label="API credentials hidden from staff" />
          </Stack>
        </Card>

        {neoWorkspace && <Card sx={{ overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ p: 2.5, pb: 2 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: 'rgba(13,148,136,.12)', color: 'secondary.main', display: 'grid', placeItems: 'center' }}><PaidOutlined /></Box>
              <Box><Typography variant="h5">Firm rates & service catalogue</Typography><Typography variant="body2" color="text.secondary">NEO quoting defaults from the October 2026 business plan.</Typography></Box>
            </Stack>
            <Chip label="Plus HST where applicable" variant="outlined" color="secondary" />
          </Stack>
          <Divider />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0,1fr))' } }}>
            {neoFirmRates.map((rate, index) => <Box key={rate.service} sx={{ px: 2.5, py: 1.7, borderBottom: index < neoFirmRates.length - 2 ? '1px solid' : { xs: '1px solid', lg: 0 }, borderRight: { lg: index % 2 === 0 ? '1px solid' : 0 }, borderColor: 'divider' }}>
              <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="baseline"><Typography sx={{ fontWeight: 750 }}>{rate.service}</Typography><Typography sx={{ color: 'primary.main', fontWeight: 800, whiteSpace: 'nowrap' }}>{rate.fee}</Typography></Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>{rate.detail}</Typography>
            </Box>)}
          </Box>
          <Alert severity="info" sx={{ m: 2, mt: 1.5 }}>These are internal quoting defaults, not automatic client charges. Confirm scope, complexity, disbursements, urgency and the written retainer before quoting. Discounts and exceptions should be documented.</Alert>
        </Card>}

        <Card component="form" onSubmit={savePassword} sx={{ p: 2.5 }}>
          <Typography variant="h5">Password and access</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>Change your own NEO workspace password. Other active sessions are revoked.</Typography>
          <Stack spacing={1.5} sx={{ maxWidth: 520 }}>
            <TextField label="Current password" type="password" value={passwords.currentPassword} onChange={event => setPasswords(current => ({ ...current, currentPassword: event.target.value }))} autoComplete="current-password" required />
            <TextField label="New password" type="password" value={passwords.newPassword} onChange={event => setPasswords(current => ({ ...current, newPassword: event.target.value }))} autoComplete="new-password" helperText="At least 12 characters, including a letter and number." required />
            <TextField label="Confirm new password" type="password" value={passwords.confirm} onChange={event => setPasswords(current => ({ ...current, confirm: event.target.value }))} autoComplete="new-password" required />
            <Button type="submit" variant="contained" disabled={saving || passwords.newPassword.length < 12}>{saving ? 'Changing…' : 'Change password'}</Button>
          </Stack>
        </Card>

        <Card sx={{ p: 2.5 }}>
          <Typography variant="h5">Appearance</Typography>
          <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={mode === 'dark'} onChange={toggleColorMode} />} label="Dark mode" />
        </Card>
      </Stack>
    </Box>
  );
}
